import { toQueryParams } from "utils/http/params";
import { dataApi } from "./DataService";
import { isoToDateString } from "utils/date";
import { markEndpointStale } from "rtkq/staleManager";
import { getQueryCacheKey } from "rtkq/getQueryCacheKey";

const makeDeleteItemAction = (api, packageName, currentQueryArgs) => {
  return api.util.updateQueryData('getApps', currentQueryArgs, (draft) => {
    const index = draft?.body?.items.findIndex(el => el.package === packageName);
    if(index !== -1) {
      draft.body.items.splice(index, 1);
    }
  });
};

const makeDeleteDetailsAction = (api, packageName) => {
  return api.util.updateQueryData('getAppVersions', {packageName}, (draft) => {
    draft.body.items = [];
  });
};

const appsApi = dataApi
  .enhanceEndpoints({addTagTypes: ['app', 'version']})
  .injectEndpoints({
    endpoints: (builder) => ({
      getApps: builder.query({
          query: (arg) => ({
              url: '/apps',
              params: toQueryParams(arg)
          }),
          providesTags: ['app']
      }),

      getAppVersions: builder.query({
          query: ({packageName}) => ({
              url: `/apps/${encodeURIComponent(packageName)}`
          }),
          providesTags: (result, error, { packageName }) => [{type: 'version', id: packageName}]
      }),

      editApp: builder.mutation({
          
        query: ({packageName, body}) => ({
          url: `/apps/${encodeURIComponent(packageName)}`,
          method: 'PUT',
          body: body
        }),
        
        async onQueryStarted({body, packageName, currentQueryArgs}, {dispatch, queryFulfilled, getState}) {
                    
          //обновляем кэш  версий приложения для данного пэкэджа
          let updateAppList = false;
          const result = dispatch (
            appsApi.util.updateQueryData('getAppVersions', {packageName}, (draft) => {
              if(draft?.body?.items?.length !== body?.items?.length) {
                updateAppList = true;
              }
              draft.body = body;                  
            })
          );

          let resultApps = null;
          if(updateAppList) { //если поменялось количество версий приложения, то нужно обновить весь список приложений, т.к. в нем есть поле latestVersion
            //помечаем все кэши списка приложений устаревшими
            dispatch(
              markEndpointStale({endpointName: 'getApps'})
            );
          }
          else {
            //обновляем текущий кэш списка приложений, если поменялось поле profileOnly
            const state = getState();
            const app = appsApi.endpoints.getApps.select(currentQueryArgs)(state)?.data?.body?.items.find(el => el.package === packageName);

            if(app?.profileOnly !== body.profileOnly) {
              resultApps = dispatch(
                appsApi.util.updateQueryData('getApps', currentQueryArgs, (draft) => {
                  const item = draft?.body?.items.find(el => el.package === packageName);
                  item && (item.profileOnly = body.profileOnly);
                })
              );
            }
          }

          
          try {
            const { data } = await queryFulfilled;
            //переписываем текущий (и единственный) кэш версий приложения ответом от сервера
            dispatch(
              appsApi.util.updateQueryData('getAppVersions', {packageName}, (draft) => {
                draft.body = data.body;
              })
            );
            if(data?.body?.items?.length === 0) {
              //если после редактирования версий приложения их не осталось, то нужно пометить кэш списка приложений устаревшим
              dispatch(
                markEndpointStale({endpointName: 'getApps'})
              );
            }
            else {
              //помечаем все оставшиеся кэши устаревшими (поля в фильтре не проверяем, т.к. фильтра по изменяемому полю нет)
              const currentCacheKey = getQueryCacheKey({endpointName: 'getApps', queryArgs: currentQueryArgs});
              dispatch(
                  markEndpointStale({
                      endpointName: 'getApps',
                      exceptCacheKey: currentCacheKey
                  })
              );
            }
          }
          catch {
            //отменяем предварительные изменения кэшей
            result.undo();
            resultApps.undo();
          }
        }
      }),

      removeApp: builder.mutation({
          query: ({packageName}) => ({
              url: `/apps/${encodeURIComponent(packageName)}`,
              method: 'DELETE'
          }),
          async onQueryStarted({packageName, currentQueryArgs}, {dispatch, queryFulfilled}) {
            const resultApps = dispatch(makeDeleteItemAction(appsApi, packageName, currentQueryArgs));
            const resultDetails = dispatch(makeDeleteDetailsAction(appsApi, packageName));
            try {
              await queryFulfilled;
              //помечаем оставшиеся кэши списка приложений устаревшими
              const currentCacheKey = getQueryCacheKey({endpointName: 'getApps', queryArgs: currentQueryArgs});  
              dispatch(
                markEndpointStale({
                    endpointName: 'getApps',
                    exceptCacheKey: currentCacheKey
                })
              );
            }
            catch {
              resultApps.undo();    
              resultDetails.undo();
            }
          }
      }),

      addAppVersion: builder.mutation({
          query: (arg) => ({
              url: `/apps`,
              method: 'POST' ,
              body: arg,
              timeout: 5 * 60 * 1000  // 5 минут   
          }),
          invalidatesTags: (result, error) => error ? [] : [{'type': 'version', 'id': result.body.packageName}, {'type': 'app'}]
      })
    })
  });

export const {
  useGetAppsQuery,
  useGetAppVersionsQuery,
  useEditAppMutation,
  useRemoveAppMutation,
  useAddAppVersionMutation
} = appsApi; 